<template>
  <div class="home">
    <!-- <v-img
          class="brighten-v-img private "
          src="../assets/serving_1920.webp"
          :srcset="`${require('../assets/serving_1920.webp')}, ${require('../assets/serving_900.webp')} 900w, ${require('../assets/serving_400.webp')} 400w`"
      > -->
    <!-- <div class="brighten-v-img private"> -->
      
    <div class="hero-wrapper">
      <!-- Desktop: Show video, Mobile: Show static poster image -->
      <video
        v-if="showDesktopVideo"
        autoplay
        muted
        loop
        playsinline
        preload="none"
        width="100%"
        height="100%"
        :poster="responsivePoster"
        class="hero-video"
        aria-hidden="true"
        fetchpriority="high"
        @canplay="onDesktopVideoCanPlay"
      >
        <source src="/kootto.webm" type="video/webm">
      </video>
      <!-- Mobile: Static poster image instead of video -->
      <img
        v-if="!showDesktopVideo || !isDesktopVideoReady"
        :src="responsivePoster"
        :alt="$t('home.hero.title_part1')"
        class="hero-image"
        width="100%"
        height="100%"
      >
      <div class="hero-overlay"></div>
      <div class="container relative z-10 d-flex flex-column align-start">
        <div class="hero-content">
          <!-- Trust Badge -->
          <div class="trust-badge mb-8 d-inline-flex align-center">
            <div class="d-flex mr-2">
              <v-icon small color="primary" v-for="n in 5" :key="n">mdi-star</v-icon>
            </div>
            <span class="white--text text-body-2 font-weight-medium">{{ $t('home.hero.trust_badge') }}</span>
          </div>

          <!-- Main Heading -->
          <h1 class="hero-title white--text mb-6">
            {{ $t('home.hero.title_part1') }} <span class="hero-title-italic">{{ $t('home.hero.title_part2') }}</span>
          </h1>

          <!-- Subheading -->
          <p class="hero-subtitle white--text mb-6">
            {{ $t('home.hero.subtitle') }}
          </p>

          <!-- Private Chef Definition (GEO: AI-citable standalone answer) -->
          <p class="white--text mb-8" style="font-size: 0.95rem; line-height: 1.7; opacity: 0.85; max-width: 560px; font-style: italic;">
            {{ $t('home.hero.definition') }}
          </p>

          <!-- CTA Buttons -->
          <div class="d-flex flex-column flex-sm-row gap-4 mb-10">
            <v-btn
              color="primary"
              x-large
              class="mr-sm-4 mb-4 mb-sm-0 hero-btn primary-btn"
              elevation="0"
              @click="$router.push({path: '/search'})"
            >
              {{ $t('home.hero.explore_menus') }}
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
            <v-btn
              color="white"
              outlined
              x-large
              class="hero-btn outline-btn"
              @click="$vuetify.goTo('.how-it-works')"
            >

              {{ $t('home.hero.how_it_works') }}
            </v-btn>
          </div>

          <!-- Key Facts -->
          <div class="stats-grid pt-6 mt-4 border-top-white-20">
            <div class="stat-item">
              <v-icon small color="primary" class="mb-1">mdi-map-marker-multiple</v-icon>
              <div class="stat-label">{{ $t('home.hero.stats.cities') }}</div>
            </div>
            <div class="stat-item">
              <v-icon small color="primary" class="mb-1">mdi-account-group</v-icon>
              <div class="stat-label">{{ $t('home.hero.stats.guests') }}</div>
            </div>
            <div class="stat-item">
              <v-icon small color="primary" class="mb-1">mdi-shield-check</v-icon>
              <div class="stat-label">{{ $t('home.hero.stats.vetting') }}</div>
            </div>
            <div class="stat-item">
              <v-icon small color="primary" class="mb-1">mdi-clock-fast</v-icon>
              <div class="stat-label">{{ $t('home.hero.stats.notice') }}</div>
            </div>
            <div class="stat-item">
              <v-icon small color="primary" class="mb-1">mdi-star</v-icon>
              <div class="stat-label">{{ $t('home.hero.stats.hosts') }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="scroll-downs" @click="$vuetify.goTo('.how-it-works')">
        <div class="mousey">
          <div class="scroller"></div>
        </div>
      </div>
    </div>

    <!-- Chef Recruitment Banner -->
    <a
      :href="`/${$route.params.language_country || 'en-AU'}/signup`"
      @click.prevent="$router.push({path: `/${$route.params.language_country || 'en-AU'}/signup`})"
      style="display: block; width: 100%; text-decoration: none; background: linear-gradient(135deg, #FF6B00 0%, #FF8500 100%); cursor: pointer;"
    >
      <v-container fluid class="py-3">
        <v-row align="center" justify="center" class="white--text text-center">
          <v-col>
            <v-icon color="white" class="mr-2">mdi-chef-hat</v-icon>
            <span class="text-h6 font-weight-bold">{{ $t('home.chef_recruitment_banner.text') }}</span>
            <v-icon color="white" class="ml-2" small>mdi-arrow-right</v-icon>
          </v-col>
        </v-row>
      </v-container>
    </a>

    <!-- Trust Indicators Section -->
    <v-container fluid style="background-color: #F8F5F2;" class="py-12">
      <v-container>
        <v-row>
          <!-- Vetted -->
          <v-col cols="12" sm="6" md="3" class="d-flex align-start trust-indicator-item">
            <v-sheet color="white" rounded class="pa-2 mr-3 elevation-1 d-flex align-center justify-center trust-icon-sheet" width="48" height="48">
              <v-icon color="primary">mdi-shield-check-outline</v-icon>
            </v-sheet>
            <div>
              <div class="font-weight-bold mb-1 body-1" style="font-family: 'DM Serif Display', serif !important;">{{ $t('home.trust_indicators[0].title') }}</div>
              <div class="caption grey--text text--darken-2" style="line-height: 1.3;">{{ $t('home.trust_indicators[0].description') }}</div>
            </div>
          </v-col>

          <!-- On-Time -->
          <v-col cols="12" sm="6" md="3" class="d-flex align-start trust-indicator-item">
            <v-sheet color="white" rounded class="pa-2 mr-3 elevation-1 d-flex align-center justify-center trust-icon-sheet" width="48" height="48">
              <v-icon color="primary">mdi-clock-outline</v-icon>
            </v-sheet>
            <div>
              <div class="font-weight-bold mb-1 body-1" style="font-family: 'DM Serif Display', serif !important;">{{ $t('home.trust_indicators[1].title') }}</div>
              <div class="caption grey--text text--darken-2" style="line-height: 1.3;">{{ $t('home.trust_indicators[1].description') }}</div>
            </div>
          </v-col>

          <!-- Quality -->
          <v-col cols="12" sm="6" md="3" class="d-flex align-start trust-indicator-item">
            <v-sheet color="white" rounded class="pa-2 mr-3 elevation-1 d-flex align-center justify-center trust-icon-sheet" width="48" height="48">
              <v-icon color="primary">mdi-ribbon</v-icon>
            </v-sheet>
            <div>
              <div class="font-weight-bold mb-1 body-1" style="font-family: 'DM Serif Display', serif !important;">{{ $t('home.trust_indicators[2].title') }}</div>
              <div class="caption grey--text text--darken-2" style="line-height: 1.3;">{{ $t('home.trust_indicators[2].description') }}</div>
            </div>
          </v-col>

          <!-- Satisfaction -->
          <v-col cols="12" sm="6" md="3" class="d-flex align-start trust-indicator-item">
            <v-sheet color="white" rounded class="pa-2 mr-3 elevation-1 d-flex align-center justify-center trust-icon-sheet" width="48" height="48">
              <v-icon color="primary">mdi-thumb-up-outline</v-icon>
            </v-sheet>
            <div>
              <div class="font-weight-bold mb-1 body-1" style="font-family: 'DM Serif Display', serif !important;">{{ $t('home.trust_indicators[3].title') }}</div>
              <div class="caption grey--text text--darken-2" style="line-height: 1.3;">{{ $t('home.trust_indicators[3].description') }}</div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <v-container fuild class="container margin-top-bottom-big how-it-works">
      <v-row align-content="center" justify="center" class="margin-top-bottom-big">
        <v-col cols="12" class="text-center">
          <div class="culinary-overline">{{ $t('home.how_it_works.overline') }}</div>
          <h2 class="stories-heading">{{ $t('home.how_it_works.heading_part1') }} <span class="community-text">Kootto</span> {{ $t('home.how_it_works.heading_part2') }}</h2>
          <p class="stories-subtitle">{{ $t('home.how_it_works.subtitle') }}</p>
        </v-col>
      </v-row>
      <v-row align-content="center" justify="center">
        <v-col class="col-sm-2 col-12 text-center scroll-animate delay-100" align-content="center" justify="center">
          <v-img src="../assets/icons/groceries.webp" height="100" width="100" contain alt="Groceries and ingredients"></v-img>
          <div class="font-weight-bold mt-2">{{ $t('home.how_it_works.features[0]') }}</div>
          <div class="caption grey--text">{{ $t('home.how_it_works.feature_descriptions[0]') }}</div>
        </v-col>
        <v-col class="col-sm-1 hidden-xs-only text-center" align-self="center">
          <v-icon x-large color="primary">mdi-arrow-right</v-icon>
        </v-col>
        <v-col class="col-sm-2 col-12 text-center scroll-animate delay-200" align-content="center" justify="center">
          <v-img src="../assets/icons/cook.webp" height="100" width="100" contain alt="Chef cooking in kitchen"></v-img>
          <div class="font-weight-bold mt-2">{{ $t('home.how_it_works.features[1]') }}</div>
          <div class="caption grey--text">{{ $t('home.how_it_works.feature_descriptions[1]') }}</div>
        </v-col>
        <v-col class="col-sm-1 hidden-xs-only text-center" align-self="center">
          <v-icon x-large color="primary">mdi-arrow-right</v-icon>
        </v-col>
        <v-col class="col-sm-2 col-12 text-center scroll-animate delay-300" align-content="center" justify="center">
          <v-img src="../assets/icons/serve.webp" height="100" width="100" contain alt="Waiter serving food"></v-img>
          <div class="font-weight-bold mt-2">{{ $t('home.how_it_works.features[2]') }}</div>
          <div class="caption grey--text">{{ $t('home.how_it_works.feature_descriptions[2]') }}</div>
        </v-col>
        <v-col class="col-sm-1 hidden-xs-only text-center" align-self="center">
          <v-icon x-large color="primary">mdi-arrow-right</v-icon>
        </v-col>
        <v-col class="col-sm-2 col-12 text-center scroll-animate delay-400" align-content="center" justify="center">
          <v-img src="../assets/icons/clean.webp" height="100" width="100" contain alt="Chef cleaning after service"></v-img>
          <div class="font-weight-bold mt-2">{{ $t('home.how_it_works.features[3]') }}</div>
          <div class="caption grey--text">{{ $t('home.how_it_works.feature_descriptions[3]') }}</div>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid class="bg-warm-white py-12">
      <v-container class="container margin-top-bottom-big">
      <v-row>
        <v-col cols="12">
           <div class="mb-8">
             <div class="culinary-overline">{{ $t('home.culinary_experiences.overline') }}</div>
             <h2 class="exceptional-heading">{{ $t('home.culinary_experiences.heading_part1') }} <span class="your-style-text">{{ $t('home.culinary_experiences.heading_part2') }}</span></h2>
             
             <div class="d-flex flex-column flex-md-row justify-space-between align-start align-md-center">
               <p class="exceptional-subtitle" style="max-width: 600px;">{{ $t('home.culinary_experiences.subtitle') }}</p>
               
               <v-btn 
                 outlined 
                 color="black"
                 class="view-services-btn ml-md-6 flex-shrink-0"
                 @click="$router.push({path: '/search'})"
               >
                 {{ $t('home.culinary_experiences.view_all_services') }}
                 <v-icon right small>mdi-arrow-right</v-icon>
               </v-btn>
             </div>
           </div>
           
           <v-row class="stretch-row">
             <!-- Course Meals (Large Left) -->
             <v-col cols="12" md="6" class="d-flex">
               <div class="service-card flex-grow-1" style="height: 660px;" @click="$router.push({path: '/search', query: {category: 'course_meal'}})">
                 <div class="most-popular-tag">{{ $t('home.culinary_experiences.course_meals.tag') }}</div>
                 <v-img src="../assets/course.webp" class="service-card-img" height="660" width="100%" alt="Course Meales culinary experience"></v-img>
                 <div class="service-card-overlay"></div>
                 <div class="service-content">
                   <h3 class="service-title">{{ $t('home.culinary_experiences.course_meals.title') }}</h3>
                   <p class="service-description">{{ $t('home.culinary_experiences.course_meals.description') }}</p>
                   <v-btn small class="book-btn elevation-0">{{ $t('home.culinary_experiences.course_meals.button') }}</v-btn>
                 </div>
               </div>
             </v-col>

             <!-- Right Column -->
             <v-col cols="12" md="6">
               <v-row class="fill-height" align-content="space-between">
                 <!-- Cocktail -->
                 <v-col cols="12" class="d-flex pb-2">
                    <div class="service-card flex-grow-1" style="height: 210px;" @click="$router.push({path: '/search', query: {category: 'cocktail'}})">
                      <v-img src="../assets/cocktail.webp" class="service-card-img" height="210" width="100%" alt="Cocktail and function catering experience"></v-img>
                      <div class="service-card-overlay"></div>
                      <div class="service-content">
                        <h3 class="service-title">{{ $t('home.culinary_experiences.cocktail.title') }}</h3>
                        <p class="service-description mb-0">{{ $t('home.culinary_experiences.cocktail.description') }}</p>
                        <v-btn small outlined color="white" class="mt-2">{{ $t('home.culinary_experiences.learn_more') }}</v-btn>
                      </div>
                    </div>
                 </v-col>
                 
                 <!-- Sharing Platters -->
                 <v-col cols="12" class="d-flex py-2">
                    <div class="service-card flex-grow-1" style="height: 210px;" @click="$router.push({path: '/search', query: {category: 'sharing_platters'}})">
                      <v-img src="../assets/sharing.webp" class="service-card-img" height="210" width="100%" alt="Sharing platters catering experience"></v-img>
                      <div class="service-card-overlay"></div>
                      <div class="service-content">
                        <h3 class="service-title">{{ $t('home.culinary_experiences.sharing.title') }}</h3>
                        <p class="service-description mb-0">{{ $t('home.culinary_experiences.sharing.description') }}</p>
                        <v-btn small outlined color="white" class="mt-2">{{ $t('home.culinary_experiences.learn_more') }}</v-btn>
                      </div>
                    </div>
                 </v-col>

                 <!-- Grazing Tables -->
                 <v-col cols="12" class="d-flex pt-2">
                    <div class="service-card flex-grow-1" style="height: 210px;" @click="$router.push({path: '/search', query: {category: 'grazing'}})">
                      <v-img src="../assets/grazing-table.webp" class="service-card-img" height="210" width="100%" alt="Grazing table culinary experience"></v-img>
                      <div class="service-card-overlay"></div>
                      <div class="service-content">
                        <h3 class="service-title">{{ $t('home.culinary_experiences.grazing.title') }}</h3>
                        <p class="service-description mb-0">{{ $t('home.culinary_experiences.grazing.description') }}</p>
                        <v-btn small outlined color="white" class="mt-2">{{ $t('home.culinary_experiences.learn_more') }}</v-btn>
                      </div>
                    </div>
                 </v-col>
               </v-row>
             </v-col>
           </v-row>
        </v-col>
      </v-row>
      </v-container>
    </v-container>
    <!-- Featured Private Chefs Section -->
    <v-container class="margin-top-bottom-big" v-if="featuredChefs && featuredChefs.length > 0">
      <v-row align-content="center" justify="center" class="margin-top-bottom-big">
        <v-col cols="12" class="text-center">
          <div class="culinary-overline">{{ $t('home.featured_chefs.overline') }}</div>
          <h2 class="stories-heading">{{ $t('home.featured_chefs.heading_part1') }} <span class="community-text">{{ $t('home.featured_chefs.heading_part2') }}</span></h2>
          <p class="stories-subtitle">{{ $t('home.featured_chefs.subtitle') }}</p>
        </v-col>
      </v-row>
      <v-row align-content="stretch" justify="center" class="margin-top-bottom-big">
        <v-col 
          cols="12" 
          md="4" 
          class="d-flex" 
          v-for="(chef, index) in featuredChefs" 
          :key="chef.id || index"
        >
          <featured-chef-card :chef="chef" />
        </v-col>
      </v-row>
      <v-row align-content="center" justify="center" class="margin-top-bottom-big">
        <v-btn
          color="primary"
          elevation="4"
          style="font-size: large;height: 50px;padding: 0 48px;"
          href="/search"
          @click.prevent="$router.push({path: '/search'})"
        >
          {{ $t('home.featured_chefs.view_all') }}
        </v-btn>
      </v-row>
    </v-container>

    <!-- Testimonials Section -->
    <v-container fluid class="bg-warm-white py-12">
      <v-container>
        <v-row align-content="center" justify="center" class="margin-top-bottom-big">
          <v-col cols="12" class="text-center">
            <div class="culinary-overline">{{ $t('home.testimonials.overline') }}</div>
            <h2 class="stories-heading">{{ $t('home.testimonials.heading_part1') }} <span class="community-text">{{ $t('home.testimonials.heading_part2') }}</span></h2>
            <p class="stories-subtitle">{{ $t('home.testimonials.subtitle') }}</p>
          </v-col>
        </v-row>

        <v-row align-content="stretch" justify="center" class="margin-top-bottom-big">
          <v-col cols="12" md="4" class="d-flex" v-for="(testimonial, index) in 3" :key="index">
          <v-card class="elevation-3 d-flex flex-column testimonial-card" style="width: 100%;">
            <v-card-text class="flex-grow-1">
              <v-icon large color="primary" class="mb-2">mdi-format-quote-open</v-icon>
              <p class="text-body-1 mb-4">{{ $t(`common.testimonials.items[${index}].quote`) }}</p>
            </v-card-text>
            <v-card-actions class="pt-0">
              <v-list-item class="grow">
                <v-list-item-avatar color="primary">
                  <span class="white--text text-h6">{{ $t(`common.testimonials.items[${index}].initials`) }}</span>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">{{ $t(`common.testimonials.items[${index}].name`) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t(`common.testimonials.items[${index}].location`) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-row align="center" justify="end">
                  <v-icon small color="amber" v-for="star in 5" :key="star">mdi-star</v-icon>
                </v-row>
              </v-list-item>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      </v-container>
    </v-container>

    <!-- FAQ Section -->
    <v-container fluid class="py-12">
      <v-container>
         <v-row>
            <!-- Left Info Column -->
            <v-col cols="12" md="6" class="pr-md-12">
               <div class="culinary-overline text-center primary--text font-weight-bold mb-2 e">{{ $t('home.faq.overline') }}</div>
               <h2 class="exceptional-heading">
                 {{ $t('home.faq.heading_part1') }} <span style="font-family: Georgia, serif; font-style: italic; color: #D76E42;">{{ $t('home.faq.heading_part2') }}</span>
               </h2>
               <p class="exceptional-subtitle">
                 {{ $t('home.faq.description') }}
               </p>

               <div class="pa-6 rounded-lg" style="background-color: #F8F5F2;">
                  <div class="font-weight-bold mb-2">{{ $t('home.faq.support_box.title') }}</div>
                  <p class="caption mb-4">{{ $t('home.faq.support_box.text') }}</p>
                  <v-btn color="primary" outlined small @click="$root.showContactUs=true">
                    {{ $t('home.faq.support_box.button_text') }}
                  </v-btn>
               </div>
            </v-col>
            
            <!-- Right Accordion Column -->
            <v-col cols="12" md="6">
              <div role="list">
                <div
                  v-for="(item, index) in 6"
                  :key="index"
                  class="faq-item"
                  role="listitem"
                >
                  <input type="checkbox" :id="`home-faq-${index}`" class="faq-toggle">
                  <label :for="`home-faq-${index}`" class="faq-question">
                    {{ $t(`home.faq.items[${index}].question`) }}
                  </label>
                  <div class="faq-answer">
                    {{ $t(`home.faq.items[${index}].answer`, {cancellationPeriod: getParameterValue('NO_CANCELLATION_PERIOD')}) }}
                  </div>
                </div>
              </div>
            </v-col>
         </v-row>
      </v-container>
    </v-container>

    <v-container fuild class="container margin-top-bottom-big">
      <v-row align-content="center" justify="center">
        <v-col class="col-12 col-sm-8 text-center" align-content="center" justify="center">
          <v-card class="pa-8 chef-signup-card" elevation="8">
            <v-icon size="64" color="primary" class="mb-4">mdi-chef-hat</v-icon>
            <h2 class="text-h4 font-weight-bold primary--text mb-4">{{ $t('home.chef_signup.heading') }}</h2>
            <p class="text-h6 mb-6">{{ $t('home.chef_signup.description') }}</p>
            
            <v-row justify="center" class="mb-6">
              <v-col cols="12" md="4" class="text-center">
                <v-icon color="primary" class="mb-2">mdi-cash-off</v-icon>
                <div class="font-weight-bold">{{ $t('home.chef_signup.benefits[0]') }}</div>
              </v-col>
              <v-col cols="12" md="4" class="text-center">
                <v-icon color="primary" class="mb-2">mdi-tag-text-outline</v-icon>
                <div class="font-weight-bold">{{ $t('home.chef_signup.benefits[1]') }}</div>
              </v-col>
              <v-col cols="12" md="4" class="text-center">
                <v-icon color="primary" class="mb-2">mdi-map-marker-star</v-icon>
                <div class="font-weight-bold">{{ $t('home.chef_signup.benefits[2]') }}</div>
              </v-col>
            </v-row>

            <v-btn 
              x-large 
              color="primary" 
              elevation="4" 
              style="font-size: large;height: 60px;padding: 0 48px;"
              class="mb-3"
              @click="click();$root.showSignup=true;$root.showSignupChef=true"
            >
              <v-icon left>mdi-chef-hat</v-icon>
              <span>{{ $t('home.chef_signup.button_text') }}</span>
            </v-btn>

            <div class="mt-3">
              <a 
                href="/signup" 
                @click.prevent="$router.push({path: '/signup'})"
                class="text-decoration-none primary--text"
              >
                <v-icon small color="primary">mdi-information-outline</v-icon>
                {{ $t('home.chef_signup.learn_more') }}
              </a>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import { mapState } from "vuex";
import FeaturedChefCard from "../components/FeaturedChefCard.vue";
import structuredDataMixin from "@/mixins/structuredData";

export default {
  components: {
    FeaturedChefCard
  },
  name: "Home",
  mixins: [structuredDataMixin],
  data() {
    return {
      // Start with poster-only render; hydrate on client before enabling desktop video.
      windowWidth: 0,
      isHydrated: false,
      isDesktopVideoReady: false
    };
  },
  methods: {
    click() {
      console.log("clicked");
    },
    ...mapMutations(["showSnackbar", "closeSnackbar"]),
    onDesktopVideoCanPlay() {
      this.isDesktopVideoReady = true;
    },
    onResize() {
      this.windowWidth = window.innerWidth;
      if (this.windowWidth < 768) {
        this.isDesktopVideoReady = false;
      }
    },
    handleScroll() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, { threshold: 0.1 });

      document.querySelectorAll('.scroll-animate').forEach((el) => {
        observer.observe(el);
      });
    }
  },
  mounted() {
    this.onResize();
    this.isHydrated = true;
    this.handleScroll();
    window.addEventListener('resize', this.onResize);

    // FAQPage schema — uses the 6 FAQ items from the home locale
    const faqItems = this.$t('home.faq.items',{cancellationPeriod: this.getParameterValue('NO_CANCELLATION_PERIOD')});
    const faqSchema = this.getFAQSchema(faqItems);
    if (faqSchema) this.setStructuredData(faqSchema);

    // Service schema — describes Kootto's national private chef service
    this.setStructuredData(this.getServiceSchema("Australia"));

    // Organization schema — entity graph for AI engine brand recognition
    this.setStructuredData(this.getOrganizationSchema());

    // Video schema - helps indexing the hero background video
    this.setStructuredData(this.getVideoSchema({
      name: "Private Chef Smoke Reveal Dessert Experience | Kootto Australia",
      description: "Experience the theater of private dining. Watch as a Kootto professional chef serves a signature smoked dessert, bringing restaurant quality flair and fine dining presentation directly to your home table.",
      thumbnailUrl: "https://kootto.com/img/home_1280.webp",
      uploadDate: "2024-12-01T08:00:00+10:00",
      contentUrl: "https://kootto.com/media/kootto.6dd248b1.webm",
      duration: "PT13S"
    }));

    // Fetch chefs
    let today = new Date();
    this.$store.dispatch('searchChefsFeatured', { today: today, featured: true }).then(() => {
      console.log("Featured chefs fetched for home page");
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  computed: {
    ...mapState({
      loggedin: "loggedin",
    }),
    isMobile() {
      return this.windowWidth < 768;
    },
    showDesktopVideo() {
      return this.isHydrated && !this.isMobile;
    },
    responsivePoster() {
      const w = this.windowWidth;
      if (w >= 1920) return '/img/home_1920.webp';
      if (w >= 1280) return '/img/home_1280.webp';
      if (w >= 900) return '/img/home_900.webp';
      return '/img/home_400.webp';
    },
    featuredChefs() {
      if (this.$store.state.searchChefsFeatured && this.$store.state.searchChefsFeatured.length > 0) {
        // Filter chefs with at least one enabled service
        const chefsWithEnabledServices = this.$store.state.searchChefsFeatured.filter(chef => {
          return chef.service && chef.service.some(service => service.enabled === true || (service.packages && service.packages.some(p => p.enabled === true)));
        });
        return chefsWithEnabledServices.slice(0, 3);
      }
      return [];
    }
  },
};
</script>

<style scoped>
.hero-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
